import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BottomNav } from '../components/custom/BottomNav';
import NotificationToggle from '../components/custom/NotificationToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, User, DollarSign, LogOut, Save, Image, Upload, Trash2, Bell, Shield, Building2, Phone, Mail, MapPin, Download, Cloud, HardDrive, CloudOff, CheckCircle, Loader2, Crown, Sparkles, Gift, Key, Eye, EyeOff, Users, Link2, UserPlus, X, Check, Clock, MessageSquare, Send, AlertTriangle, ChevronDown, BarChart3, TrendingUp, AlertCircle } from 'lucide-react';
import { saveFullBackup, getBackupInfo } from '../utils/offlineDB';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Moved outside ProfilePage to prevent re-creation on every render
const SettingsSection = ({ icon: Icon, iconBg, iconColor, title, children, value }) => (
    <AccordionItem value={value} className="border-0">
        <AccordionTrigger className="hover:no-underline px-4 py-4 bg-white rounded-2xl shadow-soft mb-2 [&[data-state=open]]:rounded-b-none [&[data-state=open]]:mb-0">
            <div className="flex items-center gap-3">
                <div className={`p-2 ${iconBg} rounded-full`}>
                    <Icon className={`h-5 w-5 ${iconColor}`} />
                </div>
                <span className="font-medium text-base">{title}</span>
            </div>
        </AccordionTrigger>
        <AccordionContent className="bg-white rounded-b-2xl shadow-soft px-4 pb-4 mb-2">
            {children}
        </AccordionContent>
    </AccordionItem>
);

export default function ProfilePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getAuthHeaders, logout, user } = useAuth();
    const fileInputRef = useRef(null);
    
    const [name, setName] = useState(user?.name || '');
    const [hourlyRate, setHourlyRate] = useState('');
    const [logo, setLogo] = useState(null);
    const [logoFilename, setLogoFilename] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [isTogglingMFA, setIsTogglingMFA] = useState(false);
    
    // Business info for invoices
    const [businessName, setBusinessName] = useState('');
    const [businessAddress, setBusinessAddress] = useState('');
    const [businessEmail, setBusinessEmail] = useState('');
    const [businessPhone, setBusinessPhone] = useState('');
    
    // Data backup states
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [isBackingUpLocal, setIsBackingUpLocal] = useState(false);
    const [localBackupInfo, setLocalBackupInfo] = useState(null);
    const [driveStatus, setDriveStatus] = useState({ connected: false, loading: true });
    const [isBackingUpDrive, setIsBackingUpDrive] = useState(false);
    const [backupReminderEnabled, setBackupReminderEnabled] = useState(false);
    
    // Subscription states
    const [subscription, setSubscription] = useState({ status: 'free', loading: true });
    const [isUpgrading, setIsUpgrading] = useState(false);
    
    // Password change states
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Account linking states
    const [linkedAccounts, setLinkedAccounts] = useState([]);
    const [pendingRequests, setPendingRequests] = useState({ incoming: [], outgoing: [] });
    const [linkEmail, setLinkEmail] = useState('');
    const [isSendingLinkRequest, setIsSendingLinkRequest] = useState(false);
    const [isLoadingLinks, setIsLoadingLinks] = useState(true);
    
    // Issue report states
    const [issueSubject, setIssueSubject] = useState('');
    const [issueDescription, setIssueDescription] = useState('');
    const [issueCategory, setIssueCategory] = useState('general');
    const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
    
    // Delete account states
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    
    // Time stats
    const [timeStats, setTimeStats] = useState(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const [userRes, logoRes, mfaRes, driveRes, subRes, linksRes, pendingRes, statsRes] = await Promise.all([
                    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/auth/logo`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/auth/mfa/status`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/drive/status`, { headers: getAuthHeaders() }).catch(() => ({ data: { connected: false } })),
                    axios.get(`${API}/subscription/status`, { headers: getAuthHeaders() }).catch(() => ({ data: { status: 'free' } })),
                    axios.get(`${API}/links`, { headers: getAuthHeaders() }).catch(() => ({ data: { linked_accounts: [] } })),
                    axios.get(`${API}/links/pending`, { headers: getAuthHeaders() }).catch(() => ({ data: { incoming: [], outgoing: [] } })),
                    axios.get(`${API}/stats/time`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
                ]);
                setName(userRes.data.name);
                setHourlyRate(userRes.data.default_hourly_rate?.toString() || '0');
                setBusinessName(userRes.data.business_name || '');
                setBusinessAddress(userRes.data.business_address || '');
                setBusinessEmail(userRes.data.business_email || '');
                setBusinessPhone(userRes.data.business_phone || '');
                setLogo(logoRes.data.logo);
                setLogoFilename(logoRes.data.filename || '');
                setMfaEnabled(mfaRes.data.mfa_enabled || false);
                setDriveStatus({ connected: driveRes.data.connected, loading: false });
                setSubscription({ ...subRes.data, loading: false });
                setLinkedAccounts(linksRes.data.linked_accounts || []);
                setPendingRequests(pendingRes.data || { incoming: [], outgoing: [] });
                setIsLoadingLinks(false);
                setTimeStats(statsRes.data);
                setIsLoadingStats(false);
                
                const backupInfo = await getBackupInfo();
                setLocalBackupInfo(backupInfo);
                
                const reminderPref = localStorage.getItem('backupReminderEnabled');
                setBackupReminderEnabled(reminderPref === 'true');
            } catch (error) {
                console.error('Failed to load settings');
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
        
        if (searchParams.get('subscription') === 'success') {
            const sessionId = searchParams.get('session_id');
            if (sessionId) verifySubscription(sessionId);
        } else if (searchParams.get('subscription') === 'cancelled') {
            toast.info('Subscription cancelled');
        }
        
        if (searchParams.get('drive_connected') === 'true') {
            toast.success('Google Drive connected successfully!');
            setDriveStatus({ connected: true, loading: false });
        } else if (searchParams.get('drive_error') === 'true') {
            toast.error('Failed to connect Google Drive');
        }
    }, [getAuthHeaders, searchParams]);
    
    const verifySubscription = async (sessionId) => {
        try {
            const response = await axios.get(`${API}/subscription/verify/${sessionId}`, { headers: getAuthHeaders() });
            if (response.data.success) {
                toast.success('🎉 Welcome to Voilaw Pro! You now have unlimited cases.');
                setSubscription({ status: 'active', subscription_end_date: response.data.subscription_end_date, loading: false });
            }
        } catch (error) {
            toast.error('Failed to verify subscription');
        }
    };

    const handleToggleMFA = async (enabled) => {
        setIsTogglingMFA(true);
        try {
            const endpoint = enabled ? '/auth/mfa/enable' : '/auth/mfa/disable';
            await axios.post(`${API}${endpoint}`, {}, { headers: getAuthHeaders() });
            setMfaEnabled(enabled);
            toast.success(enabled ? 'Two-factor authentication enabled' : 'Two-factor authentication disabled');
        } catch (error) {
            toast.error('Failed to update MFA settings');
        } finally {
            setIsTogglingMFA(false);
        }
    };

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Please fill in all password fields');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        
        setIsChangingPassword(true);
        try {
            await axios.post(`${API}/auth/change-password`, {
                current_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword
            }, { headers: getAuthHeaders() });
            
            toast.success('Password changed successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleSendLinkRequest = async () => {
        if (!linkEmail.trim()) {
            toast.error('Please enter an email address');
            return;
        }
        setIsSendingLinkRequest(true);
        try {
            await axios.post(`${API}/links/request`, { target_email: linkEmail.trim() }, { headers: getAuthHeaders() });
            toast.success('Link request sent! They will receive an email.');
            setLinkEmail('');
            const pendingRes = await axios.get(`${API}/links/pending`, { headers: getAuthHeaders() });
            setPendingRequests(pendingRes.data || { incoming: [], outgoing: [] });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send link request');
        } finally {
            setIsSendingLinkRequest(false);
        }
    };

    const handleAcceptLinkRequest = async (requestId) => {
        try {
            await axios.post(`${API}/links/accept`, { request_id: requestId }, { headers: getAuthHeaders() });
            toast.success('Link accepted! You can now see their cases.');
            const [linksRes, pendingRes] = await Promise.all([
                axios.get(`${API}/links`, { headers: getAuthHeaders() }),
                axios.get(`${API}/links/pending`, { headers: getAuthHeaders() })
            ]);
            setLinkedAccounts(linksRes.data.linked_accounts || []);
            setPendingRequests(pendingRes.data || { incoming: [], outgoing: [] });
        } catch (error) {
            toast.error('Failed to accept link request');
        }
    };

    const handleDeclineLinkRequest = async (requestId) => {
        try {
            await axios.post(`${API}/links/decline`, { request_id: requestId }, { headers: getAuthHeaders() });
            toast.success('Link request declined');
            const pendingRes = await axios.get(`${API}/links/pending`, { headers: getAuthHeaders() });
            setPendingRequests(pendingRes.data || { incoming: [], outgoing: [] });
        } catch (error) {
            toast.error('Failed to decline request');
        }
    };

    const handleCancelLinkRequest = async (requestId) => {
        try {
            await axios.post(`${API}/links/cancel`, { request_id: requestId }, { headers: getAuthHeaders() });
            toast.success('Link request cancelled');
            const pendingRes = await axios.get(`${API}/links/pending`, { headers: getAuthHeaders() });
            setPendingRequests(pendingRes.data || { incoming: [], outgoing: [] });
        } catch (error) {
            toast.error('Failed to cancel request');
        }
    };

    const handleRemoveLink = async (linkId, userName) => {
        if (!window.confirm(`Remove link with ${userName}? You will no longer see their cases.`)) return;
        try {
            await axios.delete(`${API}/links/${linkId}`, { headers: getAuthHeaders() });
            toast.success('Link removed');
            const linksRes = await axios.get(`${API}/links`, { headers: getAuthHeaders() });
            setLinkedAccounts(linksRes.data.linked_accounts || []);
        } catch (error) {
            toast.error('Failed to remove link');
        }
    };

    const handleSubmitIssue = async () => {
        if (!issueSubject.trim()) { toast.error('Please enter a subject'); return; }
        if (!issueDescription.trim()) { toast.error('Please describe your issue'); return; }
        
        setIsSubmittingIssue(true);
        try {
            await axios.post(`${API}/support/report-issue`, {
                subject: issueSubject.trim(),
                description: issueDescription.trim(),
                category: issueCategory
            }, { headers: getAuthHeaders() });
            toast.success('Report submitted! We\'ll get back to you soon.');
            setIssueSubject('');
            setIssueDescription('');
            setIssueCategory('general');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit report');
        } finally {
            setIsSubmittingIssue(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') {
            toast.error('Please type DELETE to confirm');
            return;
        }
        setIsDeletingAccount(true);
        try {
            await axios.delete(`${API}/auth/account`, { headers: getAuthHeaders() });
            toast.success('Account deleted successfully');
            logout();
            navigate('/login');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete account');
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`${API}/auth/settings`, {
                name,
                default_hourly_rate: parseFloat(hourlyRate) || 0,
                business_name: businessName,
                business_address: businessAddress,
                business_email: businessEmail,
                business_phone: businessPhone
            }, { headers: getAuthHeaders() });
            toast.success('Settings saved');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 1024 * 1024) { toast.error('Logo must be less than 1MB'); return; }
        if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
        
        setIsUploadingLogo(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${API}/auth/logo`, formData, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            const logoRes = await axios.get(`${API}/auth/logo`, { headers: getAuthHeaders() });
            setLogo(logoRes.data.logo);
            setLogoFilename(logoRes.data.filename || '');
            toast.success('Logo uploaded');
        } catch (error) {
            toast.error('Failed to upload logo');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleDeleteLogo = async () => {
        try {
            await axios.delete(`${API}/auth/logo`, { headers: getAuthHeaders() });
            setLogo(null);
            setLogoFilename('');
            toast.success('Logo removed');
        } catch (error) {
            toast.error('Failed to remove logo');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        toast.success('Logged out');
    };

    const handleExportExcel = async () => {
        setIsExportingExcel(true);
        try {
            const response = await axios.get(`${API}/export/all-data`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const today = new Date();
            link.setAttribute('download', `vlw_time_bu_${today.getFullYear()}_${String(today.getMonth() + 1).padStart(2, '0')}_${String(today.getDate()).padStart(2, '0')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Excel file exported successfully!');
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setIsExportingExcel(false);
        }
    };

    const handleExportPDF = async () => {
        setIsExportingPDF(true);
        try {
            const response = await axios.get(`${API}/export/pdf-report`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const today = new Date();
            link.setAttribute('download', `vlw_report_${today.getFullYear()}_${String(today.getMonth() + 1).padStart(2, '0')}_${String(today.getDate()).padStart(2, '0')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('PDF report exported successfully!');
        } catch (error) {
            toast.error('Failed to export PDF report');
        } finally {
            setIsExportingPDF(false);
        }
    };

    const handleLocalBackup = async () => {
        setIsBackingUpLocal(true);
        try {
            const [casesRes, remindersRes] = await Promise.all([
                axios.get(`${API}/cases`, { headers: getAuthHeaders() }),
                axios.get(`${API}/reminders`, { headers: getAuthHeaders() })
            ]);
            const timeEntries = [];
            for (const caseItem of casesRes.data) {
                const entriesRes = await axios.get(`${API}/time-entries/case/${caseItem.id}`, { headers: getAuthHeaders() });
                timeEntries.push(...entriesRes.data);
            }
            await saveFullBackup({
                cases: casesRes.data,
                timeEntries,
                reminders: remindersRes.data,
                userSettings: { name, hourlyRate, businessName, businessAddress, businessEmail, businessPhone }
            });
            const backupInfo = await getBackupInfo();
            setLocalBackupInfo(backupInfo);
            toast.success('Local backup saved!');
        } catch (error) {
            toast.error('Failed to create local backup');
        } finally {
            setIsBackingUpLocal(false);
        }
    };

    const handleConnectDrive = async () => {
        try {
            const response = await axios.get(`${API}/drive/connect`, { headers: getAuthHeaders() });
            window.location.href = response.data.authorization_url;
        } catch (error) {
            if (error.response?.data?.detail?.includes('not configured')) {
                toast.error('Google Drive not configured. Contact support to enable this feature.');
            } else {
                toast.error('Failed to connect Google Drive');
            }
        }
    };

    const handleDisconnectDrive = async () => {
        try {
            await axios.post(`${API}/drive/disconnect`, {}, { headers: getAuthHeaders() });
            setDriveStatus({ connected: false, loading: false });
            toast.success('Google Drive disconnected');
        } catch (error) {
            toast.error('Failed to disconnect Google Drive');
        }
    };

    const handleBackupToDrive = async () => {
        setIsBackingUpDrive(true);
        try {
            await axios.post(`${API}/drive/backup`, {}, { headers: getAuthHeaders() });
            toast.success('Data backed up to Google Drive!');
        } catch (error) {
            toast.error(error.response?.data?.detail?.includes('not connected') ? 'Please connect Google Drive first' : 'Failed to backup to Google Drive');
        } finally {
            setIsBackingUpDrive(false);
        }
    };

    const handleToggleBackupReminder = (enabled) => {
        setBackupReminderEnabled(enabled);
        localStorage.setItem('backupReminderEnabled', enabled.toString());
        if (enabled && 'Notification' in window && Notification.permission !== 'granted') {
            Notification.requestPermission();
        }
        toast.success(enabled ? 'Daily backup reminder enabled' : 'Daily backup reminder disabled');
    };

    const handleUpgrade = async () => {
        setIsUpgrading(true);
        try {
            const response = await axios.post(`${API}/subscription/create-checkout`, {}, { headers: getAuthHeaders() });
            window.location.href = response.data.checkout_url;
        } catch (error) {
            toast.error('Failed to start checkout. Please try again.');
            setIsUpgrading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until your current period ends.')) return;
        try {
            await axios.post(`${API}/subscription/cancel`, {}, { headers: getAuthHeaders() });
            toast.success('Subscription cancelled. You\'ll retain access until the end of your billing period.');
            setSubscription(prev => ({ ...prev, status: 'cancelled' }));
        } catch (error) {
            toast.error('Failed to cancel subscription');
        }
    };

    return (
        <div className="min-h-screen bg-background pb-safe">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/dashboard')} className="p-2 -ml-2 hover:bg-secondary rounded-full transition-colors">
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="font-heading text-2xl font-semibold">Settings</h1>
                    </div>
                </div>
            </header>

            <main className="px-4 py-6 space-y-4">
                {/* Collapsible Settings Sections - Alphabetically ordered */}
                <Accordion type="multiple" className="space-y-2">
                    
                    {/* Billing Settings */}
                    <SettingsSection icon={DollarSign} iconBg="bg-slate-200" iconColor="text-white" title="Billing Settings" value="billing">
                        <div className="space-y-3 pt-2">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2"><DollarSign className="h-4 w-4" />Default Hourly Rate</Label>
                                <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span><Input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="0.00" className="h-11 rounded-xl pl-8" data-testid="hourly-rate-input" /></div>
                                <p className="text-xs text-muted-foreground">Applied to new cases unless overridden.</p>
                            </div>
                        </div>
                    </SettingsSection>

                    {/* Data & Backup */}
                    <SettingsSection icon={HardDrive} iconBg="bg-slate-200" iconColor="text-white" title="Data & Backup" value="backup">
                        <div className="space-y-3 pt-2">
                            <div className="grid grid-cols-2 gap-2">
                                <Button variant="outline" onClick={handleExportExcel} disabled={isExportingExcel} className="h-10 rounded-xl">
                                    {isExportingExcel ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}Excel
                                </Button>
                                <Button variant="outline" onClick={handleExportPDF} disabled={isExportingPDF} className="h-10 rounded-xl">
                                    {isExportingPDF ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}PDF
                                </Button>
                            </div>
                            <Button variant="outline" onClick={handleLocalBackup} disabled={isBackingUpLocal} className="w-full h-10 rounded-xl">
                                {isBackingUpLocal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <HardDrive className="h-4 w-4 mr-2" />}
                                {isBackingUpLocal ? 'Saving...' : 'Save Local Backup'}
                            </Button>
                            {localBackupInfo && <p className="text-xs text-muted-foreground text-center">Last backup: {new Date(localBackupInfo.timestamp).toLocaleString()}</p>}
                            <div className="border-t pt-3 flex items-center justify-between">
                                <div><Label className="text-sm">Daily Backup Reminder</Label><p className="text-xs text-muted-foreground">Get reminded at 10 PM</p></div>
                                <Switch checked={backupReminderEnabled} onCheckedChange={handleToggleBackupReminder} />
                            </div>
                        </div>
                    </SettingsSection>

                    {/* Linked Accounts */}
                    <SettingsSection icon={Link2} iconBg="bg-slate-200" iconColor="text-white" title="Linked Accounts" value="linked">
                        <div className="space-y-3 pt-2">
                            <p className="text-sm text-muted-foreground">Link with other Voilaw users to share cases.</p>
                            <div className="flex gap-2">
                                <Input type="email" placeholder="Enter their email..." value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} className="h-10 rounded-xl flex-1" data-testid="link-email-input" />
                                <Button onClick={handleSendLinkRequest} disabled={isSendingLinkRequest || !linkEmail.trim()} className="h-10 rounded-xl bg-blue-600 hover:bg-blue-700 px-4" data-testid="send-link-btn">
                                    {isSendingLinkRequest ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                                </Button>
                            </div>
                            {pendingRequests.incoming?.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-amber-600 flex items-center gap-2"><Clock className="h-4 w-4" />Pending Requests</Label>
                                    {pendingRequests.incoming.map(req => (
                                        <div key={req.id} className="flex items-center justify-between p-2 bg-amber-50 rounded-xl border border-amber-100">
                                            <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{req.from_user_name}</p><p className="text-xs text-muted-foreground truncate">{req.from_user_email}</p></div>
                                            <div className="flex gap-1 flex-shrink-0">
                                                <Button size="sm" onClick={() => handleAcceptLinkRequest(req.id)} className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2"><Check className="h-3 w-3" /></Button>
                                                <Button size="sm" variant="outline" onClick={() => handleDeclineLinkRequest(req.id)} className="h-7 px-2"><X className="h-3 w-3" /></Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {linkedAccounts.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-emerald-600 flex items-center gap-2"><Users className="h-4 w-4" />Linked ({linkedAccounts.length})</Label>
                                    {linkedAccounts.map(account => (
                                        <div key={account.link_id} className="flex items-center justify-between p-2 bg-emerald-50 rounded-xl border border-emerald-100">
                                            <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{account.name}</p><p className="text-xs text-muted-foreground truncate">{account.email}</p></div>
                                            <Button size="sm" variant="ghost" onClick={() => handleRemoveLink(account.link_id, account.name)} className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50">Remove</Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </SettingsSection>

                    {/* Notifications */}
                    <SettingsSection icon={Bell} iconBg="bg-slate-200" iconColor="text-white" title="Notifications" value="notifications">
                        <div className="pt-2">
                            <p className="text-sm text-muted-foreground mb-3">Get reminders 7, 3, and 1 day before deadlines.</p>
                            <NotificationToggle onPermissionChange={(perm) => console.log('Permission:', perm)} onPrefsChange={(prefs) => console.log('Prefs:', prefs)} />
                        </div>
                    </SettingsSection>

                    {/* Profile & Business */}
                    <SettingsSection icon={User} iconBg="bg-slate-200" iconColor="text-white" title="Profile & Business" value="profile">
                        <div className="space-y-4 pt-2">
                            <div className="space-y-2">
                                <Label>Full Name</Label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-11 rounded-xl" data-testid="name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input value={user?.email || ''} disabled className="h-11 rounded-xl bg-muted" />
                            </div>
                            
                            <div className="border-t pt-4 mt-4">
                                <Label className="text-base font-medium flex items-center gap-2 mb-3"><Building2 className="h-4 w-4" /> Business Information</Label>
                                <p className="text-sm text-muted-foreground mb-3">This appears on your invoices.</p>
                                <div className="space-y-3">
                                    <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Business Name" className="h-11 rounded-xl" data-testid="business-name-input" />
                                    <textarea value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} placeholder="Business Address" rows={2} className="w-full px-4 py-2 rounded-xl border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring" data-testid="business-address-input" />
                                    <Input type="email" value={businessEmail} onChange={(e) => setBusinessEmail(e.target.value)} placeholder="Business Email" className="h-11 rounded-xl" data-testid="business-email-input" />
                                    <Input type="tel" value={businessPhone} onChange={(e) => setBusinessPhone(e.target.value)} placeholder="Business Phone" className="h-11 rounded-xl" data-testid="business-phone-input" />
                                </div>
                            </div>
                            
                            <div className="border-t pt-4 mt-4">
                                <Label className="text-base font-medium flex items-center gap-2 mb-3"><Image className="h-4 w-4" /> Invoice Logo</Label>
                                {logo ? (
                                    <div className="space-y-2">
                                        <div className="p-3 bg-slate-50 rounded-xl flex items-center justify-center"><img src={logo} alt="Logo" className="max-h-16 max-w-full object-contain" /></div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground truncate">{logoFilename}</span>
                                            <button onClick={handleDeleteLogo} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </div>
                                ) : (
                                    <button onClick={() => fileInputRef.current?.click()} disabled={isUploadingLogo} className="w-full p-4 border-2 border-dashed border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors flex flex-col items-center gap-1">
                                        <Upload className="h-6 w-6 text-slate-400" />
                                        <span className="text-sm">{isUploadingLogo ? 'Uploading...' : 'Upload Logo'}</span>
                                    </button>
                                )}
                                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                            </div>
                        </div>
                    </SettingsSection>

                    {/* Report an Issue */}
                    <SettingsSection icon={MessageSquare} iconBg="bg-slate-200" iconColor="text-white" title="Report an Issue" value="support">
                        <div className="space-y-3 pt-2">
                            <Select value={issueCategory} onValueChange={setIssueCategory}>
                                <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Category" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bug">🐛 Bug Report</SelectItem>
                                    <SelectItem value="feature">💡 Feature Request</SelectItem>
                                    <SelectItem value="billing">💳 Billing Issue</SelectItem>
                                    <SelectItem value="account">👤 Account Problem</SelectItem>
                                    <SelectItem value="general">📝 General Feedback</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input placeholder="Subject" value={issueSubject} onChange={(e) => setIssueSubject(e.target.value)} className="h-10 rounded-xl" data-testid="issue-subject-input" />
                            <Textarea placeholder="Describe your issue..." value={issueDescription} onChange={(e) => setIssueDescription(e.target.value)} rows={3} className="rounded-xl resize-none" data-testid="issue-description-input" />
                            <Button onClick={handleSubmitIssue} disabled={isSubmittingIssue || !issueSubject.trim() || !issueDescription.trim()} className="w-full h-10 rounded-xl bg-red-600 hover:bg-red-700" data-testid="submit-issue-btn">
                                {isSubmittingIssue ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Send className="h-4 w-4 mr-2" />Send Report</>}
                            </Button>
                        </div>
                    </SettingsSection>

                    {/* Security */}
                    <SettingsSection icon={Shield} iconBg="bg-slate-200" iconColor="text-white" title="Security" value="security">
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center justify-between">
                                <div><Label className="text-base">Change Password</Label><p className="text-sm text-muted-foreground">Update your account password</p></div>
                                <Button variant="outline" size="sm" onClick={() => setShowPasswordChange(!showPasswordChange)} className="rounded-xl">{showPasswordChange ? 'Cancel' : 'Change'}</Button>
                            </div>
                            {showPasswordChange && (
                                <div className="space-y-3 p-3 bg-slate-50 rounded-xl border">
                                    <div className="relative">
                                        <Input type={showCurrentPassword ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" className="h-10 rounded-xl pr-10" />
                                        <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                                    </div>
                                    <div className="relative">
                                        <Input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6 chars)" className="h-10 rounded-xl pr-10" />
                                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                                    </div>
                                    <div className="relative">
                                        <Input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className={`h-10 rounded-xl pr-10 ${confirmPassword && newPassword !== confirmPassword ? 'border-red-300' : ''}`} />
                                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                                    </div>
                                    <Button onClick={handleChangePassword} disabled={isChangingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword} className="w-full h-10 rounded-xl bg-violet-600 hover:bg-violet-700">
                                        {isChangingPassword ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</> : 'Update Password'}
                                    </Button>
                                </div>
                            )}
                            <div className="border-t pt-4 flex items-center justify-between">
                                <div><Label className="text-base">Two-Factor Authentication</Label><p className="text-sm text-muted-foreground">Email verification codes on login</p></div>
                                <Switch checked={mfaEnabled} onCheckedChange={handleToggleMFA} disabled={isTogglingMFA} data-testid="mfa-toggle" />
                            </div>
                        </div>
                    </SettingsSection>

                    {/* Subscription */}
                    <SettingsSection icon={Crown} iconBg="bg-slate-200" iconColor="text-white" title="Subscription" value="subscription">
                        <div className="space-y-3 pt-2">
                            {subscription.loading ? (
                                <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-violet-500" /><span className="text-sm text-violet-600">Loading...</span></div>
                            ) : subscription.status === 'active' ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-700">Status</span>
                                        <Badge className="bg-gradient-to-r from-violet-500 to-purple-500 text-white border-0">Pro</Badge>
                                    </div>
                                    <p className="text-sm text-slate-600">Active until {new Date(subscription.subscription_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.</p>
                                    <Button onClick={handleCancelSubscription} variant="outline" className="w-full h-10 rounded-xl border-red-300 text-red-600 hover:bg-red-50">Cancel Subscription</Button>
                                </>
                            ) : subscription.status === 'complimentary' || subscription.is_complimentary ? (
                                <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-3">
                                    <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-violet-600" /><span className="font-semibold text-violet-700">Complimentary Pro</span></div>
                                    <p className="text-sm text-violet-600 mt-1">Unlimited cases included.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-700">Status</span>
                                        <Badge className="bg-violet-100 text-violet-600 border-violet-200">{subscription.status === 'cancelled' ? 'Cancelled' : 'Free'}</Badge>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-200">
                                        <div className="p-1.5 bg-gradient-to-br from-violet-200 to-purple-200 rounded-full mt-0.5"><Crown className="h-4 w-4 text-violet-600" /></div>
                                        <div><p className="font-medium text-slate-800">Pro Plan - $28/year</p><p className="text-sm text-violet-600/70">Unlimited cases, priority support</p></div>
                                    </div>
                                    <Button onClick={handleUpgrade} disabled={isUpgrading} className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold" data-testid="upgrade-btn">
                                        {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}Upgrade to Pro
                                    </Button>
                                </>
                            )}
                        </div>
                    </SettingsSection>

                    {/* Time Stats */}
                    <SettingsSection icon={BarChart3} iconBg="bg-slate-200" iconColor="text-white" title="Time Stats" value="stats">
                        <div className="space-y-4 pt-2">
                            {isLoadingStats ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                                </div>
                            ) : timeStats ? (
                                <>
                                    {/* Summary Cards */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
                                            <p className="text-xs text-indigo-600 font-medium">Total Hours Logged</p>
                                            <p className="text-2xl font-bold text-indigo-700">{timeStats.total_hours}h</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
                                            <p className="text-xs text-emerald-600 font-medium">Total Billed</p>
                                            <p className="text-2xl font-bold text-emerald-700">${timeStats.total_billed?.toLocaleString()}</p>
                                        </div>
                                    </div>
                                    
                                    {/* This Week */}
                                    <div className="bg-slate-50 rounded-xl p-4">
                                        <p className="text-sm font-medium text-slate-700 mb-2">This Week</p>
                                        <div className="flex justify-between">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Hours</p>
                                                <p className="text-lg font-semibold">{timeStats.this_week_hours}h</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Billed</p>
                                                <p className="text-lg font-semibold text-emerald-600">${timeStats.this_week_amount?.toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Active Cases</p>
                                                <p className="text-lg font-semibold">{timeStats.active_cases}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Unbilled Work Alert */}
                                    {timeStats.unbilled_completed_todos > 0 && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="font-medium text-amber-800">Unbilled Work Detected</p>
                                                    <p className="text-sm text-amber-700 mt-1">
                                                        <span className="font-bold">{timeStats.unbilled_completed_todos}</span> completed tasks weren't converted to billable time.
                                                    </p>
                                                    <p className="text-sm text-amber-600 mt-1">
                                                        Estimated lost revenue: <span className="font-semibold">${timeStats.estimated_lost_revenue?.toLocaleString()}</span>
                                                    </p>
                                                    <p className="text-xs text-amber-500 mt-2">
                                                        Tip: Use "Bill Time" when completing tasks to capture all billable work.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Todo Conversion Stats */}
                                    <div className={`rounded-xl p-4 ${
                                        timeStats.billing_rate_percent >= 80 ? 'bg-emerald-50 border border-emerald-100' :
                                        timeStats.billing_rate_percent >= 50 ? 'bg-amber-50 border border-amber-100' :
                                        'bg-red-50 border border-red-100'
                                    }`}>
                                        <p className="text-sm font-medium text-slate-700 mb-3">Todo → Billable Conversion</p>
                                        <div className="grid grid-cols-2 gap-4 mb-3">
                                            <div>
                                                <p className="text-xs text-muted-foreground">Tasks Billed</p>
                                                <p className={`text-lg font-bold ${
                                                    timeStats.billing_rate_percent >= 80 ? 'text-emerald-600' :
                                                    timeStats.billing_rate_percent >= 50 ? 'text-amber-600' :
                                                    'text-red-600'
                                                }`}>
                                                    {timeStats.todos_converted_to_time}/{timeStats.todos_with_case_completed || 0}
                                                    <span className="text-sm font-medium ml-1">
                                                        ({timeStats.billing_rate_percent || 0}%)
                                                    </span>
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Revenue from Tasks</p>
                                                <p className="text-lg font-semibold text-emerald-600">${timeStats.todos_converted_amount?.toLocaleString()}</p>
                                            </div>
                                        </div>
                                        
                                        {/* Conditional messaging based on billing rate */}
                                        {timeStats.todos_with_case_completed > 0 && (
                                            <div className={`text-sm mt-2 pt-2 border-t ${
                                                timeStats.billing_rate_percent >= 80 ? 'border-emerald-200 text-emerald-700' :
                                                timeStats.billing_rate_percent >= 50 ? 'border-amber-200 text-amber-700' :
                                                'border-red-200 text-red-700'
                                            }`}>
                                                {timeStats.billing_rate_percent >= 80 ? (
                                                    <p>✓ Great job! You're capturing most of your billable work.</p>
                                                ) : timeStats.billing_rate_percent >= 50 ? (
                                                    <p>⚠️ You're missing out on billing for {100 - timeStats.billing_rate_percent}% of your case-related tasks. Use "Bill Time" when completing todos to capture revenue.</p>
                                                ) : (
                                                    <p>⚠️ <strong>Only {timeStats.billing_rate_percent}% of your work is being billed.</strong> You may be leaving significant revenue on the table. Remember to click "Bill Time" instead of "Mark Complete" for billable tasks.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Waived Time */}
                                    {timeStats.total_waived > 0 && (
                                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                            <span className="text-sm text-muted-foreground">Waived/Pro Bono</span>
                                            <span className="font-medium text-slate-600">${timeStats.total_waived?.toLocaleString()}</span>
                                        </div>
                                    )}
                                    
                                    {/* Top Task Types */}
                                    {timeStats.task_breakdown?.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium text-slate-700 mb-2">Top Activities</p>
                                            <div className="space-y-2">
                                                {timeStats.task_breakdown.slice(0, 5).map((task, idx) => (
                                                    <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded-lg">
                                                        <span className="text-muted-foreground truncate flex-1">{task.task_type}</span>
                                                        <div className="flex items-center gap-3 flex-shrink-0">
                                                            <span className="font-medium">{task.hours}h</span>
                                                            <span className="text-emerald-600 font-medium">${task.amount}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-center text-muted-foreground py-4">No time data available yet</p>
                            )}
                        </div>
                    </SettingsSection>
                </Accordion>

                {/* Save Button */}
                <Button onClick={handleSave} disabled={isSaving} className="w-full h-12 rounded-full text-base font-semibold bg-violet-500 hover:bg-violet-600" data-testid="save-settings-btn">
                    <Save className="h-5 w-5 mr-2" />{isSaving ? 'Saving...' : 'Save Settings'}
                </Button>

                {/* Sign Out */}
                <button onClick={handleLogout} className="w-full py-3 text-destructive font-medium rounded-2xl hover:bg-red-50 transition-colors flex items-center justify-center gap-2" data-testid="logout-btn">
                    <LogOut className="h-5 w-5" />Sign Out
                </button>

                {/* Danger Zone - Delete Account */}
                <Accordion type="single" collapsible className="mt-4">
                    <AccordionItem value="danger" className="border-0">
                        <AccordionTrigger className="hover:no-underline px-4 py-4 bg-red-50 border-2 border-red-200 rounded-2xl shadow-soft mb-2 [&[data-state=open]]:rounded-b-none [&[data-state=open]]:mb-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-500 rounded-full">
                                    <AlertTriangle className="h-5 w-5 text-white" />
                                </div>
                                <span className="font-medium text-base text-red-700">Danger Zone</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className="bg-red-50 border-2 border-t-0 border-red-200 rounded-b-2xl shadow-soft px-4 pb-4 mb-2">
                            <p className="text-sm text-red-600/80 mb-4">Permanently delete your account and all associated data. This action cannot be undone.</p>
                            {!showDeleteConfirm ? (
                                <Button variant="outline" onClick={() => setShowDeleteConfirm(true)} className="w-full h-10 rounded-xl border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700" data-testid="show-delete-btn">
                                    <Trash2 className="h-4 w-4 mr-2" />Delete My Account
                                </Button>
                            ) : (
                                <div className="space-y-3 p-3 bg-white rounded-xl border border-red-200">
                                    <p className="text-sm font-medium text-red-700">Type DELETE to confirm:</p>
                                    <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())} placeholder="DELETE" className="h-10 rounded-xl border-red-200 focus:border-red-400" data-testid="delete-confirm-input" />
                                    <div className="flex gap-2">
                                        <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }} className="flex-1 h-10 rounded-xl">Cancel</Button>
                                        <Button onClick={handleDeleteAccount} disabled={isDeletingAccount || deleteConfirmText !== 'DELETE'} className="flex-1 h-10 rounded-xl bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                                            {isDeletingAccount ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Account'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                <div className="h-24" />
            </main>

            <BottomNav />
        </div>
    );
}
